import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Grid,
  Divider,
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  CheckCircle as SuccessIcon,
  Cancel as FailIcon,
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { formatPercentage, getSuccessRateColor } from './utils';

/**
 * ExecutionStatisticsCard Component
 *
 * Displays execution statistics for a version.
 *
 * @param {Object} props
 * @param {Object} props.selectedVersion - Selected version data
 */
function ExecutionStatisticsCard({ selectedVersion }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <TimelineIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">Execution Statistics</Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box textAlign="center">
              <Typography variant="h4" color="primary.main">
                {selectedVersion.total_executions || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Executions
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box textAlign="center">
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={0.5}
              >
                <SuccessIcon color="success" />
                <Typography variant="h4" color="success.main">
                  {selectedVersion.successful_executions || 0}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Successful
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box textAlign="center">
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={0.5}
              >
                <FailIcon color="error" />
                <Typography variant="h4" color="error.main">
                  {selectedVersion.failed_executions || 0}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Failed
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box textAlign="right">
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                gap={0.5}
              >
                <TrendingUpIcon
                  sx={{
                    color: theme => {
                      const color = getSuccessRateColor(
                        selectedVersion.success_rate_percentage
                      );
                      return (
                        theme.palette[color]?.main ||
                        theme.palette.text.secondary
                      );
                    },
                  }}
                />
                <Typography
                  variant="h4"
                  sx={{
                    color: theme => {
                      const color = getSuccessRateColor(
                        selectedVersion.success_rate_percentage
                      );
                      return (
                        theme.palette[color]?.main || theme.palette.text.primary
                      );
                    },
                  }}
                >
                  {formatPercentage(selectedVersion.success_rate_percentage)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Success Rate
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <SpeedIcon color="action" />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Avg Execution Time
                </Typography>
                <Typography variant="h6">
                  {selectedVersion.avg_execution_time
                    ? `${selectedVersion.avg_execution_time.toFixed(2)}s`
                    : 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Grid>
          {selectedVersion.min_execution_time !== undefined &&
            selectedVersion.max_execution_time !== undefined && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <SpeedIcon color="action" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Execution Time Range
                    </Typography>
                    <Typography variant="h6">
                      {selectedVersion.min_execution_time.toFixed(2)}s -{' '}
                      {selectedVersion.max_execution_time.toFixed(2)}s
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
        </Grid>
      </CardContent>
    </Card>
  );
}

export default ExecutionStatisticsCard;
